/**
 * Промысловые зоны и подзоны на карте: начертание и подпись.
 *
 * Чистая логика без MapLibre и без React — её и проверяют тесты. Укладка слоя
 * лежит в `districtsLayer.ts`, запрос по кадру — в `useDistrictsLayer.ts`.
 *
 * ЗАЛИВКИ НЕТ, и это решение. Карта раздела «Аналитика» существует ради поля
 * прогноза; заливка районов перекрыла бы ровно то, ради чего её открывают.
 * Границу показывает линия, район называет номер — как на «Рабочей схеме ГМИ»,
 * с которой капитан и сверяется.
 */
import { mapColorOf } from '../brand/mapColors'
import { OVERLAY_PREFIX } from './layerOrder'

/** id слоя района по роли. */
export const districtLayerId = (role: string): string =>
  `${OVERLAY_PREFIX}fd-${role}`

/** Ниже этого зума номера сливаются в кашу: на обзорном зуме район
 *  различает форма, а не подпись. */
export const DISTRICT_LABEL_MIN_ZOOM = 4.5

/** Штрих. Граница района — не рубеж государства и не измерение: длинный
 *  пунктир отличает её и от сплошной линии ИЭЗ, и от точечной спорной. */
export const DISTRICT_DASH: [number, number] = [4, 2.5]

export interface DistrictProps {
  code: string
  /** Номер «Рабочей схемы ГМИ». ПУСТ у района, которого нет в приказе № 285:
   *  у Центрально-Беринговоморского подрайона номера схемы не знает ни один
   *  источник, и выдуманный остался бы у капитана на карте как настоящий. */
  map_code: string | null
  name: string
  zone: string | null
  kind: string
  osm_code: number | null
  note: string | null
  label: [number, number]
}

/**
 * Имя района для человека.
 *
 * ИМЯ ЗОНЫ ОБЯЗАТЕЛЬНО, когда оно есть: «Тихоокеанская» в приказе — ДВЕ разные
 * подзоны, Северо-Курильская и Южно-Курильская, и голое имя не отвечает ни на
 * что. У подрайона (61.52) зоны нет вовсе, и приписывать её нечего.
 */
export function districtTitle(p: Pick<DistrictProps, 'name' | 'zone'>): string {
  return p.zone ? `${p.name} (${p.zone})` : p.name
}

/**
 * Точки подписей отдельной коллекцией.
 *
 * Ярлык ставится в точке, которую посчитал СЕРВЕР по видимой части района
 * (`ST_PointOnSurface` пересечения с кадром). Отдав подпись самому полигону,
 * мы получили бы её в центре ВСЕГО района — то есть за экраном у любого
 * района, ушедшего краем за кадр.
 */
export function districtLabels(
  data: GeoJSON.FeatureCollection,
): GeoJSON.FeatureCollection {
  const features = (data.features ?? [])
    .map(f => {
      const p = f.properties as unknown as DistrictProps | null
      if (!p?.label || p.label.length !== 2) return null
      return {
        type: 'Feature' as const,
        // Без номера схемы подписываем ИМЕНЕМ. Пустой ярлык MapLibre рисует
        // как ничто: район остался бы на карте безымянной линией, и отличить
        // его от границы, нарисованной по ошибке, было бы нечем.
        properties: { _label: p.map_code || p.name, code: p.code },
        geometry: { type: 'Point' as const, coordinates: p.label },
      }
    })
    .filter((f): f is NonNullable<typeof f> => f !== null)
  return { type: 'FeatureCollection', features }
}

/** Цвет линии и подписи. Один на все районы: они равноправны, и раскрасив их
 *  по-разному, мы дали бы цвету смысл, которого у него нет. */
export const districtColor = (): string => mapColorOf('fishing-district')

export interface DistrictAnswer {
  status: 'district' | 'outside' | 'unknown'
  map_code?: string | null
  name?: string
  zone?: string | null
  osm_code?: number | null
  note?: string | null
}

/**
 * Строка карточки отсчёта: в каком промысловом районе точка.
 *
 * ТРИ ответа, и два из них путать нельзя. `outside` — настоящий ответ
 * (открытая часть морей, воды за пределами промысловых зон), и его надо
 * произнести; `unknown` — сюда не смотрели, и молчание тут честнее выдумки.
 *
 * Номер схемы идёт ПЕРВЫМ: капитан сверяется с «Рабочей схемой ГМИ», где
 * подписаны именно номера, а имена в приказе повторяются («Тихоокеанская» —
 * две разные подзоны).
 */
export function districtLine(
  d: DistrictAnswer | null | undefined,
  t: (key: string) => string,
): string | null {
  if (!d || d.status === 'unknown') return null
  if (d.status === 'outside') return t('districts.status.outside')
  const title = d.zone ? `${d.name} (${d.zone})` : d.name
  return [d.map_code, title].filter(Boolean).join(' · ')
}
